import React, { useState, useEffect } from 'react';
import { Paper, Typography, Stack, Box, Button, Alert, TextField, Chip, CircularProgress } from '@mui/material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Edit } from '@mui/icons-material';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import axios from '../../api/axios';
import { useNotification } from '../../utils/useNotification';

function CodeTracingComponent({ example, onAnswerSubmitted, isTeacher = false, embedded = false, useChatPanelForAnswer = false }) {
  const [selected, setSelected] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { showPointsNotification, NotificationComponent } = useNotification();
  
  // 获取已提交的答案（复习/嵌入模式下不预填，由 AI 解答）
  useEffect(() => {
    if (embedded) return;
    const fetchSubmittedAnswer = async () => {
      try {
        const questionId = `notebook_${example.id}`;
        const response = await axios.get(`/submissions/question/${questionId}`);
        const submissions = response.data || [];
        if (submissions.length > 0) {
          const latestSubmission = submissions[0];
          try {
            const answerData = JSON.parse(latestSubmission.code);
            if (answerData.answer) {
              // 选择题模式
              if (example.options && example.options.length > 0) {
                const answerLetter = answerData.answer.toUpperCase();
                const answerIndex = answerLetter.charCodeAt(0) - 'A'.charCodeAt(0);
                setSelected(answerIndex);
              } else {
                // 简答题模式
                setAnswer(answerData.answer);
              }
              setHasSubmitted(true);
              setSubmissionResult({
                status: latestSubmission.status,
                isCorrect: latestSubmission.status === 'passed',
                score: latestSubmission.score
              });
            }
          } catch (e) {
            // 如果解析失败，尝试直接使用（简答题模式）
            if (latestSubmission.code && !example.options) {
              setAnswer(latestSubmission.code);
              setHasSubmitted(true);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching submitted answer:', error);
      }
    };
    
    if (example.id) {
      fetchSubmittedAnswer();
    }
  }, [example.id, example.options, embedded]);
  
  const CodeBlock = ({ code: codeContent, language = 'python' }) => {
    const cleanCode = codeContent.replace(/```python\n?/g, '').replace(/```/g, '').trim();
    return (
      <Box 
        sx={{ 
          my: 2,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          backgroundColor: '#ffffff',
        }}
      >
        <SyntaxHighlighter 
          language={language} 
          style={oneLight}
          customStyle={{
            margin: 0,
            padding: '16px',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            borderRadius: '8px',
            backgroundColor: '#ffffff',
          }}
          showLineNumbers={false}
          wrapLines={true}
          wrapLongLines={true}
        >
          {cleanCode}
        </SyntaxHighlighter>
      </Box>
    );
  };
  
  const handleSubmit = async () => {
    // 选择题模式
    if (example.options && example.options.length > 0) {
      if (selected === null) {
        alert('请先选择一个答案');
        return;
      }
      
      setSubmitting(true);
      setSubmissionResult(null);
      
      try {
        const answerLetter = String.fromCharCode(65 + selected);
        const response = await axios.post(`/notebook-examples/${example.id}/submit`, {
          answer: answerLetter
        });
        
        const submission = response.data.submission;
        if (embedded) {
          setSelected(null);
          setHasSubmitted(false);
          setSubmissionResult(null);
          setShowAnswer(false);
          setIsEditing(false);
        } else {
          setSubmissionResult(submission);
          setShowAnswer(true);
          setHasSubmitted(true);
          setIsEditing(false);
        }
        
        if (!embedded && submission.status === 'passed' && submission.score > 0 && !isTeacher) {
          showPointsNotification(submission.score);
        }
        
        if (onAnswerSubmitted) {
          if (embedded) {
            onAnswerSubmitted(answerLetter, { isCorrect: submission.status === 'passed', score: submission.score });
          } else {
            onAnswerSubmitted(answerLetter);
          }
        }
      } catch (error) {
        console.error('Error submitting answer:', error);
        setSubmissionResult({
          status: 'error',
          isCorrect: false,
          error: error.response?.data?.error || '提交失败，请重试'
        });
      } finally {
        setSubmitting(false);
      }
    } else {
      // 简答题模式
      if (!answer.trim()) {
        alert('请输入答案');
        return;
      }
      
      setSubmitting(true);
      setSubmissionResult(null);
      
      try {
        const response = await axios.post(`/notebook-examples/${example.id}/submit`, {
          answer: answer.trim()
        });
        
        const submission = response.data.submission;
        if (embedded) {
          setAnswer('');
          setHasSubmitted(false);
          setSubmissionResult(null);
          setShowAnswer(false);
          setIsEditing(false);
        } else {
          setSubmissionResult(submission);
          setShowAnswer(true);
          setHasSubmitted(true);
          setIsEditing(false);
        }
        
        if (!embedded && submission.status === 'passed' && submission.score > 0 && !isTeacher) {
          showPointsNotification(submission.score);
        }
        
        if (onAnswerSubmitted) {
          if (embedded) {
            onAnswerSubmitted(answer.trim(), { isCorrect: submission.status === 'passed', score: submission.score });
          } else {
            onAnswerSubmitted(answer.trim());
          }
        }
      } catch (error) {
        console.error('Error submitting answer:', error);
        setSubmissionResult({
          status: 'error',
          isCorrect: false,
          error: error.response?.data?.error || '提交失败，请重试'
        });
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <Paper
      elevation={embedded ? 0 : undefined}
      sx={{ 
        p: 5, 
        ...(embedded && { pl: 0, pr: 0 }),
        display: 'flex', 
        flexDirection: 'column', 
        width: '100%',
        maxWidth: '900px',
        mx: 'auto',
        boxSizing: 'border-box',
        ...(embedded && { bgcolor: 'transparent' }),
      }}
    >
      <Box sx={{ 
        mb: 3,
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        '& *': {
          maxWidth: '100%',
        },
      }}>
        <MarkdownRenderer content={example.question} />
      </Box>
      
      {example.codeAnswer && (
        <Box sx={{ mb: 3, flexShrink: 0 }}>
          <CodeBlock code={example.codeAnswer} language="python" />
        </Box>
      )}
      
      {/* Multiple Choice Mode */}
      {example.options && example.options.length > 0 && (
        <Stack spacing={1.5} sx={{ mb: 3, flexShrink: 0 }}>
          {example.options.map((option, idx) => {
            const isCorrect = example.correctAnswer && 
              (example.correctAnswer === String.fromCharCode(65 + idx) || 
               example.correctAnswer === idx.toString());
            const isSelected = selected === idx;
            const isWrongSelected = !embedded && submissionResult && !submissionResult.isCorrect && isSelected;
            const showCorrectWrong = !embedded && showAnswer;
            
            return (
              <Box
                key={idx}
                onClick={() => (!showCorrectWrong || isEditing) && setSelected(idx)}
                sx={{
                  p: 2,
                  border: 2,
                  borderColor: showCorrectWrong && isCorrect 
                    ? 'success.main' 
                    : isWrongSelected
                      ? 'error.main'
                    : isSelected 
                      ? 'primary.main' 
                      : 'divider',
                  borderRadius: 2,
                  bgcolor: showCorrectWrong && isCorrect 
                    ? 'success.light' 
                    : isWrongSelected
                      ? 'error.light'
                    : isSelected 
                      ? 'primary.light' 
                      : 'transparent',
                  cursor: (showCorrectWrong && !isEditing) ? 'default' : 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': showCorrectWrong ? {} : {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  }
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <MarkdownRenderer content={option?.trim() ?? ''} />
                  </Box>
                  {showCorrectWrong && isCorrect && (
                    <Chip label="正确答案" color="success" size="small" />
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
      
      {/* Short Answer Mode */}
      {!example.options && (
        <>
          {useChatPanelForAnswer ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              请在右侧输入框输入答案并发送
            </Typography>
          ) : (
            (!showAnswer || isEditing) && (
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="请输入答案..."
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                sx={{ mb: 3, flexShrink: 0 }}
                variant="outlined"
                disabled={submitting}
              />
            )
          )}
          
          {hasSubmitted && !isEditing && (
            <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
              <Box component="div">
                <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                <strong>已提交的答案：</strong>
                </Typography>
                <Box component="div" sx={{ mt: 1, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  {answer}
                </Box>
              </Box>
            </Alert>
          )}
          
          {showAnswer && example.answer && (
            <Alert severity="success" sx={{ mb: 2, flexShrink: 0 }}>
              <Box component="div" sx={{ '& > *': { mb: 0.5 } }}>
                <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                  <strong>参考答案：</strong>
                </Typography>
                <MarkdownRenderer content={example.answer} />
              </Box>
            </Alert>
          )}
        </>
      )}
      
      {!embedded && submissionResult && (
        <Alert 
          severity={submissionResult.status === 'submitted' ? 'info' : submissionResult.isCorrect ? 'success' : 'error'} 
          sx={{ mb: 2, flexShrink: 0 }}
        >
          <Typography variant="body2">
            {submissionResult.status === 'submitted' ? '✓ 答案已提交，等待评分' : 
             submissionResult.isCorrect ? '✓ 回答正确！' : '✗ 回答错误'}
            {submissionResult.error && ` - ${submissionResult.error}`}
          </Typography>
        </Alert>
      )}
      
      {!embedded && example.explanation && showAnswer && (
        <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
          <Box component="div" sx={{ '& > *': { mb: 0.5 } }}>
            <Typography variant="body2" component="div" sx={{ mb: 1 }}>
              <strong>解释：</strong>
            </Typography>
            <MarkdownRenderer content={example.explanation} />
          </Box>
        </Alert>
      )}
      
      {!useChatPanelForAnswer && (
        <Stack direction="row" spacing={2} sx={{ mt: 2, flexShrink: 0 }}>
          {(!hasSubmitted || isEditing || embedded) ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                submitting || 
                (example.options && example.options.length > 0 ? selected === null : !answer.trim())
              }
              sx={{ minWidth: 120 }}
            >
              {submitting ? <CircularProgress size={20} /> : hasSubmitted ? '重新提交' : '提交答案'}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => {
                  setIsEditing(true);
                  setShowAnswer(false);
                }}
              >
                修改答案
              </Button>
              <Button
                variant={showAnswer ? 'outlined' : 'contained'}
                onClick={() => setShowAnswer(!showAnswer)}
                disabled={!isTeacher && !hasSubmitted && !isEditing}
              >
                {showAnswer ? '隐藏答案' : '显示答案'}
              </Button>
            </>
          )}
        </Stack>
      )}
      
      <NotificationComponent />
    </Paper>
  );
}

export default CodeTracingComponent;

